import dayjs, { type Dayjs } from 'dayjs';
import type { ChatUserModel } from 'types/chat-users/chat-user';

/** מינימום 5 דקות מהעכשיו */
export const SCHEDULE_MIN_LEAD_MS = 5 * 60 * 1000;

/** מקסימום 23:29:00 מהודעה נכנסת אחרונה */
export const SCHEDULE_MAX_AFTER_INCOMING_MS = (23 * 60 + 29) * 60 * 1000;

export type ChatScheduleBounds = {
  min: Date;
  max: Date | null;
  hasIncomingAnchor: boolean;
  schedulingAllowed: boolean;
};

export type ChatSchedulePresetId = '5m' | '15m' | '1h' | 'morning8' | 'morning9' | 'morning10';

export type ChatSchedulePreset = {
  id: ChatSchedulePresetId;
  at: Date;
  labelHe: string;
};

function parseFlexibleDate(value: unknown): Date | null {
  if (value == null) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  const s = String(value).trim();
  if (!s) return null;
  const direct = new Date(s);
  if (!Number.isNaN(direct.getTime())) return direct;
  const heMatch = s.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?\s*[|]?\s*(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (heMatch) {
    const day = Number(heMatch[1]);
    const month = Number(heMatch[2]) - 1;
    const year = heMatch[3]
      ? Number(heMatch[3].length === 2 ? `20${heMatch[3]}` : heMatch[3])
      : new Date().getFullYear();
    const hour = Number(heMatch[4]);
    const minute = Number(heMatch[5]);
    const second = heMatch[6] ? Number(heMatch[6]) : 0;
    const d = new Date(year, month, day, hour, minute, second);
    if (!Number.isNaN(d.getTime())) return d;
  }
  const djs = dayjs(s);
  return djs.isValid() ? djs.toDate() : null;
}

/** מועד ההודעה הנכנסת האחרונה — בסיס לחישוב תקרת התזמון */
export function resolveLastIncomingMessageInstant(chatUser: ChatUserModel | null | undefined): Date | null {
  if (!chatUser) return null;
  const lastObj = chatUser.LastIncomingMessage as Record<string, unknown> | null | undefined;
  const candidates: unknown[] = [
    chatUser.IncomingLastMessageDate,
    lastObj?.dateCreated,
    lastObj?.DateCreateRaw,
    lastObj?.createdAt,
    lastObj?.DateCreated,
  ];
  for (const c of candidates) {
    const d = parseFlexibleDate(c);
    if (d) return d;
  }
  return null;
}

export function getChatScheduleBounds(
  chatUser: ChatUserModel | null | undefined,
  now: Date = new Date(),
): ChatScheduleBounds {
  const min = new Date(now.getTime() + SCHEDULE_MIN_LEAD_MS);
  const incoming = resolveLastIncomingMessageInstant(chatUser);
  if (!incoming) {
    return {
      min,
      max: null,
      hasIncomingAnchor: false,
      schedulingAllowed: false,
    };
  }
  const max = new Date(incoming.getTime() + SCHEDULE_MAX_AFTER_INCOMING_MS);
  const schedulingAllowed = max.getTime() >= min.getTime();
  return {
    min,
    max,
    hasIncomingAnchor: true,
    schedulingAllowed,
  };
}

export function isScheduleInstantAllowed(at: Date, bounds: ChatScheduleBounds): boolean {
  if (!bounds.schedulingAllowed) return false;
  if (at.getTime() < bounds.min.getTime()) return false;
  if (bounds.max && at.getTime() > bounds.max.getTime()) return false;
  return true;
}

function nextMorningSlot(hour: number, notBefore: Date): Date | null {
  const base = new Date(notBefore);
  for (let dayOffset = 0; dayOffset < 3; dayOffset += 1) {
    const d = new Date(base);
    d.setDate(base.getDate() + dayOffset);
    d.setHours(hour, 0, 0, 0);
    if (d.getTime() >= notBefore.getTime()) return d;
  }
  return null;
}

export function buildChatSchedulePresets(
  bounds: ChatScheduleBounds,
  now: Date = new Date(),
): ChatSchedulePreset[] {
  if (!bounds.schedulingAllowed) return [];

  const presets: ChatSchedulePreset[] = [];
  const offsetPresets: { id: ChatSchedulePresetId; minutes: number; labelHe: string }[] = [
    { id: '5m', minutes: 5, labelHe: 'עוד 5 דקות' },
    { id: '15m', minutes: 15, labelHe: 'עוד רבע שעה' },
    { id: '1h', minutes: 60, labelHe: 'עוד שעה' },
  ];

  for (const p of offsetPresets) {
    const at = new Date(now.getTime() + p.minutes * 60_000);
    if (isScheduleInstantAllowed(at, bounds)) {
      presets.push({ id: p.id, at, labelHe: p.labelHe });
    }
  }

  const morningHours: { id: ChatSchedulePresetId; hour: number }[] = [
    { id: 'morning8', hour: 8 },
    { id: 'morning9', hour: 9 },
    { id: 'morning10', hour: 10 },
  ];
  for (const { id, hour } of morningHours) {
    const at = nextMorningSlot(hour, bounds.min);
    if (at && isScheduleInstantAllowed(at, bounds)) {
      presets.push({
        id,
        at,
        labelHe: `ב-${String(hour).padStart(2, '0')}:00`,
      });
    }
  }

  return presets;
}

export function formatScheduledSendDateForApi(at: Date): string {
  return dayjs(at).format('YYYY-MM-DDTHH:mm');
}

export function formatScheduleDisplayHe(at: Date): string {
  return dayjs(at).format('DD/MM/YYYY HH:mm');
}

export function clampScheduleDayjs(value: Dayjs | null, bounds: ChatScheduleBounds): Dayjs | null {
  if (!value || !value.isValid()) return null;
  let v = value;
  const minD = dayjs(bounds.min);
  if (v.isBefore(minD)) v = minD;
  if (bounds.max) {
    const maxD = dayjs(bounds.max);
    if (v.isAfter(maxD)) v = maxD;
  }
  return v;
}
